import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Mail, Phone } from "lucide-react";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  travel_dates: "",
  party_size: "",
  interests: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setDone(true);
      setForm(initial);
      toast.success("Thank you — your travel designer will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#b8965a]/40 text-[#1f3a2e] placeholder-[#6b7a70]/70 font-body-lux font-light py-3 focus:outline-none focus:border-[#b8965a] transition-colors duration-300";

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="relative py-24 md:py-32 bg-[#f4ecdd]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <p className="font-body-lux text-xs uppercase tracking-[0.35em] text-[#9c7d44] mb-8">
              Book a Consultation
            </p>
            <h2 className="font-serif-lux font-light leading-[1.08] tracking-tight text-[#1f3a2e] text-4xl sm:text-5xl">
              Let&apos;s design your
              <br />
              <span className="italic text-[#b8965a]">perfect UAE holiday.</span>
            </h2>
            <p className="font-body-lux text-base font-light text-[#47564d] mt-8 leading-relaxed max-w-md">
              Tell us a little about you. A dedicated travel designer will
              personally reach out to begin shaping your journey.
            </p>

            <div className="mt-12 space-y-5">
              <a
                data-testid="contact-email-link"
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-4 group"
              >
                <Mail size={20} strokeWidth={1.3} className="text-[#b8965a]" />
                <span className="font-body-lux text-[#1f3a2e] group-hover:text-[#9c7d44] transition-colors duration-300">
                  {CONTACT.email}
                </span>
              </a>
              <a
                data-testid="contact-phone-link"
                href={`tel:${CONTACT.phone}`}
                className="flex items-center gap-4 group"
              >
                <Phone size={20} strokeWidth={1.3} className="text-[#b8965a]" />
                <span className="font-body-lux text-[#1f3a2e] group-hover:text-[#9c7d44] transition-colors duration-300">
                  {CONTACT.phoneDisplay}
                </span>
              </a>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 lg:pl-8"
          >
            {done ? (
              <div
                data-testid="contact-success"
                className="border border-[#b8965a]/40 bg-[#fdfbf7] p-12 h-full flex flex-col justify-center"
              >
                <h3 className="font-serif-lux text-4xl text-[#b8965a] mb-4">
                  Thank you.
                </h3>
                <p className="font-body-lux text-[#47564d] font-light leading-relaxed">
                  Your request has been received. A travel designer will be in
                  touch with you shortly to begin crafting your bespoke UAE
                  holiday.
                </p>
                <button
                  data-testid="contact-reset-btn"
                  onClick={() => setDone(false)}
                  className="font-body-lux text-sm text-[#9c7d44] mt-8 self-start nav-link"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form
                data-testid="consultation-form"
                onSubmit={submit}
                className="grid grid-cols-1 sm:grid-cols-2 gap-8"
              >
                <div>
                  <label className="font-body-lux text-xs uppercase tracking-[0.2em] text-[#6b7a70] mb-2 block">
                    Full Name *
                  </label>
                  <input
                    data-testid="input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="font-body-lux text-xs uppercase tracking-[0.2em] text-[#6b7a70] mb-2 block">
                    Email *
                  </label>
                  <input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="font-body-lux text-xs uppercase tracking-[0.2em] text-[#6b7a70] mb-2 block">
                    Phone *
                  </label>
                  <input
                    data-testid="input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+971 …"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="font-body-lux text-xs uppercase tracking-[0.2em] text-[#6b7a70] mb-2 block">
                    Travel Dates
                  </label>
                  <input
                    data-testid="input-dates"
                    type="text"
                    value={form.travel_dates}
                    onChange={update("travel_dates")}
                    placeholder="e.g. March 2026"
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-body-lux text-xs uppercase tracking-[0.2em] text-[#6b7a70] mb-2 block">
                    Travellers
                  </label>
                  <input
                    data-testid="input-party"
                    type="text"
                    value={form.party_size}
                    onChange={update("party_size")}
                    placeholder="e.g. 2 adults, 1 child"
                    className={inputClass}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-body-lux text-xs uppercase tracking-[0.2em] text-[#6b7a70] mb-2 block">
                    Your Interests & Travel Style
                  </label>
                  <textarea
                    data-testid="input-interests"
                    value={form.interests}
                    onChange={update("interests")}
                    placeholder="Tell us what an unforgettable holiday looks like to you…"
                    rows={3}
                    className={`${inputClass} resize-none`}
                  />
                </div>
                <div className="sm:col-span-2 mt-2">
                  <button
                    data-testid="submit-consultation-btn"
                    type="submit"
                    disabled={loading}
                    className="font-body-lux text-sm tracking-wide bg-[#b8965a] text-[#fdfbf7] px-10 py-4 hover:bg-[#9c7d44] transition-colors duration-300 disabled:opacity-60 flex items-center gap-3"
                  >
                    {loading && <Loader2 size={18} className="animate-spin" />}
                    {loading ? "Sending…" : "Request My Consultation"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
